import { LoadingOutlined } from "@ant-design/icons";
import {
  Card,
  Divider,
  Empty,
  Flex,
  Image,
  Layout,
  Spin,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import siteLogo from "../../assets/site_logo.png";
import siteLogoDark from "../../assets/site_logo_dark.png";
import { CONFIGS } from "../../configs/configs";

const { Text } = Typography;

// Decode Base64 token to get invoice number
function decodeReceiptToken(token) {
  try {
    let base64 = token.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    const decoded = atob(base64);
    const payload = JSON.parse(decoded);
    return payload.invoice;
  } catch (error) {
    console.error('Error decoding receipt token:', error);
    return null;
  }
}

export default function ReceiptPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('id');
  const invoiceNumber = token ? decodeReceiptToken(token) : null;
  const [receiptData, setReceiptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReceiptData = async () => {
      if (!invoiceNumber) return;

      try {
        setLoading(true);

        // Call the public receipt endpoint
        const response = await fetch(
          `${CONFIGS.backend_url}transaction/getPublicReceiptDetails`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              invoice_no: invoiceNumber,
            }),
          }
        );

        const result = await response.json();

        if (!response.ok || !result.status) {
          setError(result.error || "Failed to fetch receipt details");
          return;
        }

        setReceiptData(result.data);
      } catch (error) {
        console.error("Error fetching receipt:", error);
        setError(error.message || "Network error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchReceiptData();
  }, [invoiceNumber]);

  // Determine theme based on system preference since we don't have context
  const isLightTheme = window.matchMedia("(prefers-color-scheme: light)").matches;

  // Extract items from plates
  const plateItems = receiptData?.items?.flatMap((p) => p.items) || [];

  return (
    <Layout style={{ height: "100vh" }}>
      <Layout.Content
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {loading || error || !receiptData ? (
          <Flex align="center" justify="center" vertical gap={80}>
            <Image
              src={isLightTheme ? siteLogo : siteLogoDark}
              preview={false}
              width="80%"
              style={{ maxWidth: "450px" }}
            />

            <StatusContent
              loading={loading}
              error={error}
              receiptData={receiptData}
            />
          </Flex>
        ) : (
          <Card
            className="dark-card"
            style={{ margin: "0 auto", maxWidth: 500, width: "90%" }}
          >
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              {receiptData.merchant?.merchant_name && (
                <h2>{receiptData.merchant.merchant_name}</h2>
              )}
              <h2>RECEIPT</h2>
              <p>Invoice No: {receiptData.transaction.invoice_no}</p>
              <p>{receiptData.transaction.transaction_datetime}</p>
            </div>

            <Divider />

            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: "bold",
                }}
              >
                <span>Qty Items</span>
                <span>Price</span>
              </div>
              <Divider />

              {plateItems.map((item, index) => (
                <div key={index} style={{ marginBottom: 10 }}>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span>
                      {item.quantity}x{item.category}
                    </span>
                    <span>{item.unit_price.toFixed(2)}</span>
                  </div>
                  <div
                    style={{ marginLeft: 20, fontSize: "0.9em", color: "#666" }}
                  >
                    - {item.sub_category}
                  </div>
                </div>
              ))}
            </div>

            <Divider />

            <div style={{ textAlign: "right" }}>
              {receiptData.transaction.sst_type === "Exclusive" ? (
                <>
                  <p>Subtotal: {receiptData.transaction.subtotal?.toFixed(2)}</p>
                  <p>
                    SST ({receiptData.transaction.sst_rate}%):{" "}
                    {receiptData.transaction.sst_amount?.toFixed(2)}
                  </p>
                  <p>
                    Round Adj: {receiptData.transaction.round_adj_amount?.toFixed(2)}
                  </p>
                  <Divider />
                </>
              ) : (
                <p>Inclusive of SST ({receiptData.transaction.sst_rate}%)</p>
              )}
              <p style={{ fontWeight: "bold", fontSize: "1.2em" }}>
                Total: {receiptData.transaction.amount?.toFixed(2)}
              </p>
              {receiptData.transaction.payment_method === "Cash" && (
                <>
                  <p>Cash: {receiptData.transaction.pay_amount?.toFixed(2)}</p>
                  <p>Change: {receiptData.transaction.change_amount?.toFixed(2)}</p>
                </>
              )}
              <Divider />
              <p>Payment method: {receiptData.transaction.payment_method}</p>
            </div>
          </Card>
        )}
      </Layout.Content>
    </Layout>
  );
}

function StatusContent({ loading, error, receiptData }) {
  if (loading) {
    return (
      <Spin indicator={<LoadingOutlined spin style={{ fontSize: 96 }} />} />
    );
  }
  if (error) {
    return <Text type="danger">{error}</Text>;
  }
  if (!receiptData) {
    return <Empty description="No receipt data available" />;
  }
  return <></>;
}
